import { useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertTriangle, IconCheck, IconDownload, IconExternalLink } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { Route, Routes } from 'react-router';
import dayjs from 'dayjs';
import { hoy } from '@gestionpyme/dominio';
import { api, descargar } from '../api';
import { avisarError, avisarOk, Cabecera, CampoEuros, EJERCICIO_ACTUAL, euros, fechaCorta, OPCIONES_EJERCICIO, TRIMESTRE_ACTUAL } from '../comun';
import type { Calculo130, Declaracion, Obligacion, Resumen } from '../tipos';

const aISO = (d: Date | string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
const SEDE_130 = 'https://sede.agenciatributaria.gob.es/Sede/procedimientoini/G601.shtml';

export function PaginaFiscal() {
  return (
    <Routes>
      <Route path="130/:ejercicio/:trimestre" element={<VistaFiscal />} />
      <Route path="*" element={<VistaFiscal />} />
    </Routes>
  );
}

function VistaFiscal() {
  const p = useParams();
  const navegar = useNavigate();
  const [ejercicio, setEjercicio] = useState(p.ejercicio ?? String(TRIMESTRE_ACTUAL === 1 ? EJERCICIO_ACTUAL - 1 : EJERCICIO_ACTUAL));
  const [trimestre, setTrimestre] = useState(p.trimestre ?? String(TRIMESTRE_ACTUAL === 1 ? 4 : TRIMESTRE_ACTUAL - 1));

  return (
    <>
      <Cabecera titulo="Impuestos" descripcion="Modelo 130, libros registro para Hacienda, REPEP y calendario">
        <Select w={110} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} aria-label="Ejercicio" />
      </Cabecera>
      <Tabs defaultValue="130" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="130">Modelo 130</Tabs.Tab>
          <Tabs.Tab value="calendario">Calendario</Tabs.Tab>
          <Tabs.Tab value="declaraciones">Declaraciones</Tabs.Tab>
          <Tabs.Tab value="repep">REPEP / 425</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="130">
          <Group mb="md">
            {['1', '2', '3', '4'].map((t) => (
              <Button
                key={t}
                variant={t === trimestre ? 'filled' : 'default'}
                onClick={() => {
                  setTrimestre(t);
                  navegar(`/fiscal/130/${ejercicio}/${t}`, { replace: true });
                }}
              >
                {t}T
              </Button>
            ))}
          </Group>
          <Modelo130 ejercicio={Number(ejercicio)} trimestre={Number(trimestre)} />
        </Tabs.Panel>
        <Tabs.Panel value="calendario">
          <Calendario ejercicio={Number(ejercicio)} />
        </Tabs.Panel>
        <Tabs.Panel value="declaraciones">
          <Declaraciones ejercicio={Number(ejercicio)} />
        </Tabs.Panel>
        <Tabs.Panel value="repep">
          <Repep ejercicio={Number(ejercicio)} />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

// ── Modelo 130 ──────────────────────────────────────────────────────────────

const CASILLAS: [string, string][] = [
  ['01', 'Ingresos computables (acumulado)'],
  ['02', 'Gastos deducibles (acumulado)'],
  ['03', 'Rendimiento neto'],
  ['04', 'Pago fraccionado (% del rendimiento)'],
  ['05', 'Pagos fraccionados de trimestres anteriores'],
  ['06', 'Retenciones soportadas (acumulado)'],
  ['07', 'Resultado sección I'],
  ['12', 'Total liquidación'],
  ['13', 'Minoración art. 110.3.c'],
  ['14', 'Diferencia'],
  ['15', 'Resultados negativos anteriores'],
  ['16', 'Deducción vivienda habitual'],
  ['17', 'Resultado'],
  ['18', 'Complementaria: ingresado antes'],
  ['19', 'RESULTADO DE LA AUTOLIQUIDACIÓN'],
];

const DESTINO = {
  ingresar: { texto: 'A ingresar', color: 'red' },
  a_deducir: { texto: 'Negativa: a deducir en los siguientes trimestres', color: 'teal' },
  negativa: { texto: 'Negativa', color: 'teal' },
  cero: { texto: 'Negativa (cero)', color: 'teal' },
} as const;

function Modelo130({ ejercicio, trimestre }: { ejercicio: number; trimestre: number }) {
  const qc = useQueryClient();
  const calculo = useQuery({
    queryKey: ['130', ejercicio, trimestre],
    queryFn: () => api.get<Calculo130>(`/fiscal/modelo130/${ejercicio}/${trimestre}`),
    retry: false,
  });
  const [presentar, setPresentar] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const c = calculo.data;

  if (calculo.error) return <Alert color="orange">{(calculo.error as Error).message}</Alert>;
  if (!c) return <Text>Calculando…</Text>;

  const bajarLibros = async () => {
    setDescargando(true);
    try {
      const comp = await api.get<{ incidencias: { nivel: string; mensaje: string }[]; avisos: string[] }>(`/fiscal/libros/${ejercicio}/${trimestre}/comprobacion`);
      const errores = comp.incidencias.filter((i) => i.nivel === 'error');
      if (errores.length) avisarError(new Error(`El libro tiene ${errores.length} error(es): ${errores.slice(0, 3).map((e) => e.mensaje).join('; ')}`));
      await descargar(`/fiscal/libros/${ejercicio}/${trimestre}`, `libros-${ejercicio}-${trimestre}T.xlsx`);
    } catch (e) {
      avisarError(e);
    } finally {
      setDescargando(false);
    }
  };

  const d = DESTINO[c.destino];
  return (
    <Stack>
      {c.presentada && (
        <Alert color="teal" icon={<IconCheck size={18} />}>
          Presentado el {fechaCorta(c.presentada.fechaPresentacion)} ({c.presentada.origen === 'externa' ? 'registrado a mano' : 'calculado aquí'}). El periodo está bloqueado.
          {c.presentada.requiereRevision && ' Hay cambios posteriores: revisa si procede una complementaria.'}
        </Alert>
      )}
      {c.avisos.map((a) => (
        <Alert key={a} color="yellow" icon={<IconAlertTriangle size={18} />}>
          {a}
        </Alert>
      ))}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>
              Modelo 130 · {trimestre}T {ejercicio}
            </Text>
            {c.plazo && (
              <Badge variant="light">
                Plazo: {fechaCorta(c.plazo.desde)} – {fechaCorta(c.plazo.hasta)}
              </Badge>
            )}
          </Group>
          <Table>
            <Table.Tbody>
              {CASILLAS.map(([k, nombre]) => (
                <Table.Tr key={k} style={k === '19' ? { fontWeight: 700 } : undefined}>
                  <Table.Td w={40}>
                    <Text size="xs" c="dimmed">
                      {k}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" title={c.explicacion[k]}>
                      {nombre}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{euros(c.casillas[k])}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" mt="sm">
            <Badge color={d.color} size="lg">
              {d.texto}
            </Badge>
            <Text fw={700} fz="xl">
              {euros(c.casillas['19'])}
            </Text>
          </Group>
          {c.provisionDificilJustificacion > 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              La casilla 02 incluye {euros(c.provisionDificilJustificacion)} de gastos de difícil justificación (estimación directa simplificada).
            </Text>
          )}
        </Card>

        <Stack>
          <Card withBorder>
            <Text fw={600} mb="xs">
              Cómo presentarlo
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                1. Descarga el libro registro (formato oficial de la AEAT). Incluye todo el año hasta el final del trimestre.
              </Text>
              <Button leftSection={<IconDownload size={16} />} onClick={bajarLibros} loading={descargando} variant="light">
                Descargar libros registro (XLSX)
              </Button>
              <Text size="sm">
                2. En la sede de la AEAT, abre el modelo 130 y pulsa «Importar libro de registro»: las casillas se rellenan solas. Comprueba que coinciden con las de aquí.
              </Text>
              <Button component="a" href={SEDE_130} target="_blank" rel="noopener" variant="subtle" leftSection={<IconExternalLink size={16} />}>
                Abrir el modelo 130 en la sede
              </Button>
              <Text size="sm">3. Cuando lo presentes, regístralo aquí para bloquear el periodo y que cuente en el siguiente trimestre.</Text>
              <Button leftSection={<IconCheck size={16} />} disabled={!!c.presentada} onClick={() => setPresentar(true)}>
                Marcar como presentado
              </Button>
            </Stack>
          </Card>
          <Accordion variant="contained">
            <Accordion.Item value="ingresos">
              <Accordion.Control>Ingresos incluidos ({c.detalle.ingresos.length})</Accordion.Control>
              <Accordion.Panel>
                <ListaDetalle filas={c.detalle.ingresos.map((i) => ({ id: i.id, fecha: i.fecha, texto: i.descripcion, importe: i.computable }))} />
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="gastos">
              <Accordion.Control>Gastos incluidos ({c.detalle.gastos.length})</Accordion.Control>
              <Accordion.Panel>
                <ListaDetalle filas={c.detalle.gastos.map((g) => ({ id: g.id, fecha: g.fecha, texto: g.descripcion, importe: g.deducible }))} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      </SimpleGrid>

      <Modal opened={presentar} onClose={() => setPresentar(false)} title={`Registrar el 130 del ${trimestre}T ${ejercicio} como presentado`}>
        <Presentar
          resultado={c.casillas['19'] ?? 0}
          onConfirmar={async (fecha, notas) => {
            try {
              await api.post(`/fiscal/modelo130/${ejercicio}/${trimestre}/presentar`, { fechaPresentacion: fecha, notas });
              avisarOk('Declaración registrada. El periodo queda bloqueado.');
              setPresentar(false);
              await qc.invalidateQueries();
            } catch (e) {
              avisarError(e);
            }
          }}
        />
      </Modal>
    </Stack>
  );
}

function ListaDetalle({ filas }: { filas: { id: string; fecha: string; texto: string; importe: number }[] }) {
  return (
    <Table>
      <Table.Tbody>
        {filas.map((f) => (
          <Table.Tr key={f.id}>
            <Table.Td>{fechaCorta(f.fecha)}</Table.Td>
            <Table.Td>
              <Text size="sm" lineClamp={1}>
                {f.texto}
              </Text>
            </Table.Td>
            <Table.Td ta="right">{euros(f.importe)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function Presentar({ resultado, onConfirmar }: { resultado: number; onConfirmar: (fecha: string, notas: string | null) => Promise<void> }) {
  const [fecha, setFecha] = useState<string | null>(hoy());
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  return (
    <Stack>
      <Text size="sm">
        Se guardará el cálculo actual (resultado {euros(resultado)}) y se bloquearán los movimientos del periodo. Comprueba antes que coincide con lo presentado en la sede.
      </Text>
      <DateInput label="Fecha de presentación" value={fecha} onChange={(v) => setFecha(aISO(v))} valueFormat="DD/MM/YYYY" required />
      <Textarea label="Notas (nº de justificante, etc.)" value={notas} onChange={(e) => setNotas(e.currentTarget.value)} />
      <Group justify="flex-end">
        <Button
          loading={enviando}
          disabled={!fecha}
          onClick={async () => {
            setEnviando(true);
            await onConfirmar(fecha!, notas || null);
            setEnviando(false);
          }}
        >
          Registrar
        </Button>
      </Group>
    </Stack>
  );
}

// ── Calendario ──────────────────────────────────────────────────────────────

const ESTADO_OBLIGACION = {
  presentada: { texto: 'Presentada', color: 'teal' },
  vencida: { texto: 'Vencida', color: 'red' },
  en_plazo: { texto: 'En plazo', color: 'orange' },
  futura: { texto: 'Próxima', color: 'gray' },
} as const;

function Calendario({ ejercicio }: { ejercicio: number }) {
  const cal = useQuery({ queryKey: ['calendario-ej', ejercicio], queryFn: () => api.get<{ obligaciones: Obligacion[]; aviso?: string }>(`/fiscal/calendario/${ejercicio}`) });
  if (cal.data?.aviso) return <Alert color="orange">{cal.data.aviso}</Alert>;
  return (
    <Paper withBorder>
      <Table.ScrollContainer minWidth={600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Obligación</Table.Th>
              <Table.Th>Organismo</Table.Th>
              <Table.Th>Plazo</Table.Th>
              <Table.Th>Estado</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(cal.data?.obligaciones ?? []).map((o) => (
              <Table.Tr key={o.clave}>
                <Table.Td>
                  <Text size="sm">{o.titulo}</Text>
                  {o.nota && (
                    <Text size="xs" c="dimmed">
                      {o.nota}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{o.organismo}</Table.Td>
                <Table.Td>
                  {fechaCorta(o.desde)} – {fechaCorta(o.hasta)}
                </Table.Td>
                <Table.Td>
                  <Badge color={o.condicional && o.estado !== 'presentada' ? 'gray' : ESTADO_OBLIGACION[o.estado].color} variant="light">
                    {o.condicional && o.estado !== 'presentada' ? 'Solo si procede' : ESTADO_OBLIGACION[o.estado].texto}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
}

// ── Declaraciones ───────────────────────────────────────────────────────────

function Declaraciones({ ejercicio }: { ejercicio: number }) {
  const qc = useQueryClient();
  const lista = useQuery({ queryKey: ['declaraciones', ejercicio], queryFn: () => api.get<Declaracion[]>(`/fiscal/declaraciones?ejercicio=${ejercicio}`) });
  const [nueva, setNueva] = useState(false);
  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Registra aquí también los modelos que presentaste fuera de la aplicación (por ejemplo, los 130 anteriores): el 130 los necesita porque es acumulativo.
        </Text>
        <Button variant="light" onClick={() => setNueva(true)}>
          Registrar declaración presentada
        </Button>
      </Group>
      <Paper withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Modelo</Table.Th>
              <Table.Th>Periodo</Table.Th>
              <Table.Th>Presentada</Table.Th>
              <Table.Th ta="right">Resultado</Table.Th>
              <Table.Th>Origen</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(lista.data ?? []).map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>{d.modelo}</Table.Td>
                <Table.Td>{d.periodo === '0A' ? 'Anual' : d.periodo}</Table.Td>
                <Table.Td>{fechaCorta(d.fechaPresentacion)}</Table.Td>
                <Table.Td ta="right">{euros(d.resultado)}</Table.Td>
                <Table.Td>{d.origen === 'externa' ? 'Registrada a mano' : 'Calculada aquí'}</Table.Td>
                <Table.Td>
                  {d.requiereRevision && (
                    <Button
                      size="xs"
                      color="orange"
                      variant="light"
                      onClick={async () => {
                        await api.post(`/fiscal/declaraciones/${d.id}/revisada`).catch(avisarError);
                        await qc.invalidateQueries({ queryKey: ['declaraciones'] });
                      }}
                    >
                      Cambios posteriores: marcar revisada
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <Modal opened={nueva} onClose={() => setNueva(false)} title="Registrar declaración presentada fuera" size="lg">
        <FormularioExterna
          ejercicio={ejercicio}
          onListo={async () => {
            setNueva(false);
            await qc.invalidateQueries();
          }}
        />
      </Modal>
    </Stack>
  );
}

function FormularioExterna({ ejercicio, onListo }: { ejercicio: number; onListo: () => void }) {
  const [d, setD] = useState({ modelo: '130', periodo: '1T', fecha: null as string | null, resultado: null as number | null, c07: null as number | null, c15: 0 as number | null, notas: '' });
  const [enviando, setEnviando] = useState(false);
  const es130 = d.modelo === '130';
  const guardar = async () => {
    setEnviando(true);
    try {
      await api.post('/fiscal/declaraciones/externa', {
        modelo: d.modelo,
        ejercicio,
        periodo: d.periodo,
        fechaPresentacion: d.fecha,
        resultado: d.resultado,
        casillas: es130 ? { '07': d.c07 ?? 0, '15': d.c15 ?? 0, '16': 0, '19': d.resultado ?? 0 } : {},
        notas: d.notas || null,
      });
      avisarOk('Declaración registrada');
      onListo();
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Stack>
      <SimpleGrid cols={3}>
        <Select label="Modelo" data={['130', '425', '100', '412', '347']} value={d.modelo} onChange={(v) => setD({ ...d, modelo: v ?? '130', periodo: v === '130' ? '1T' : '0A' })} allowDeselect={false} />
        <Select label="Periodo" data={es130 ? ['1T', '2T', '3T', '4T'] : [{ value: '0A', label: 'Anual' }]} value={d.periodo} onChange={(v) => setD({ ...d, periodo: v ?? '1T' })} allowDeselect={false} />
        <NumberInput label="Ejercicio" value={ejercicio} disabled />
      </SimpleGrid>
      <DateInput label="Fecha de presentación" value={d.fecha} onChange={(v) => setD({ ...d, fecha: aISO(v) })} valueFormat="DD/MM/YYYY" required />
      <CampoEuros label={es130 ? 'Resultado (casilla 19)' : 'Resultado'} description="En negativo si salió a deducir" valor={d.resultado} onCambio={(v) => setD({ ...d, resultado: v })} allowNegative />
      {es130 && (
        <SimpleGrid cols={2}>
          <CampoEuros label="Casilla 07" description="Resultado de la sección I" valor={d.c07} onCambio={(v) => setD({ ...d, c07: v })} allowNegative />
          <CampoEuros label="Casilla 15" description="Negativos anteriores deducidos" valor={d.c15} onCambio={(v) => setD({ ...d, c15: v })} />
        </SimpleGrid>
      )}
      <Textarea label="Notas" value={d.notas} onChange={(e) => setD({ ...d, notas: e.currentTarget.value })} />
      <Group justify="flex-end">
        <Button onClick={guardar} loading={enviando} disabled={!d.fecha || d.resultado === null || (es130 && d.c07 === null)}>
          Registrar
        </Button>
      </Group>
    </Stack>
  );
}

// ── REPEP y modelo 425 ──────────────────────────────────────────────────────

function Repep({ ejercicio }: { ejercicio: number }) {
  const resumen = useQuery({ queryKey: ['resumen', ejercicio], queryFn: () => api.get<Resumen>(`/fiscal/resumen/${ejercicio}`) });
  const m425 = useQuery({ queryKey: ['425', ejercicio], queryFn: () => api.get<{ casillas: Record<string, number>; nota: string }>(`/fiscal/modelo425/${ejercicio}`) });
  const r = resumen.data?.repep;
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Card withBorder>
        <Text fw={700} mb="xs">
          Volumen de operaciones {ejercicio}
        </Text>
        {r ? (
          <Stack gap={4}>
            <Text size="sm">Real: {euros(r.volumenReal)}</Text>
            <Text size="sm">Anualizado: {euros(r.volumenAnualizado)}</Text>
            <Text size="sm">
              Límite para seguir en el REPEP en {ejercicio + 1}: {euros(r.umbralSiguiente)} ({(r.porcentajeUmbral / 100).toFixed(1)} %)
            </Text>
            {r.superaUmbral && <Alert color="red">Se supera el límite: el año siguiente habría que repercutir IGIC.</Alert>}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            El perfil fiscal de {ejercicio} no está en el REPEP.
          </Text>
        )}
      </Card>
      <Card withBorder>
        <Text fw={700} mb="xs">
          Modelo 425 · {ejercicio} (se presenta en enero de {ejercicio + 1})
        </Text>
        <Table>
          <Table.Tbody>
            {[
              ['142', 'Operaciones exentas por el REPEP'],
              ['143', 'Exentas por comerciante minorista'],
              ['144', 'No sujetas'],
              ['146', 'Transmisión de patrimonio'],
              ['147', 'Total volumen de operaciones'],
            ].map(([k, n]) => (
              <Table.Tr key={k}>
                <Table.Td w={50}>{k}</Table.Td>
                <Table.Td>{n}</Table.Td>
                <Table.Td ta="right">{euros(m425.data?.casillas[k!])}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text size="xs" c="dimmed" mt="xs">
          {m425.data?.nota}
        </Text>
      </Card>
    </SimpleGrid>
  );
}
